import {
  AfterViewInit,
  Component,
  ElementRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import Highcharts from "highcharts";
import { environment } from "../../../environments/environment";
import dashboardDataJson from "../../../json/re_dsm_meter_data.json";
import qcaDashboardDataJson from "../../../json/qca-dashboard.mock.json";
import qcaManagementDataJson from "../../../json/qca-management.mock.json";
import pssManagementDataJson from "../../../json/pss-management.mock.json";
import * as topojson from 'topojson-client';
import { DsmBlockService } from '../../core/services/dsm-block.service';
import { ThemeService } from '../../services/theme.service';
import { weatherImpactedPss } from '../weather-dashboard/weather-data';

type Kpi = {
  label: string;
  value: string;
  detail: string;
  icon: string;
  tone: string;
};
type EntityCard = {
  title: string;
  total: string;
  rows: { label: string; value: string; tone?: string }[];
  icon: string;
  tone: string;
};
type Alert = {
  level: "critical" | "warning" | "info" | "success";
  title: string;
  detail: string;
  time: string;
};
type Activity = { icon: string; title: string; detail: string; tone: string };
type Deviation = {
  code: string;
  qca: string;
  mw: string;
  percent: string;
  status: "Over Injection" | "Under Injection";
};
type TopPssDeviation = {
  rank: number;
  qcaCode: string;
  pssCode: string;
  pssName: string;
  qcaName: string;
  schedule: number;
  actual: number;
  deviation: number;
  deviationAmount: number;
  deviationPercent: number;
};
type QcaDashboardScheduleRow = {
  qcaCode: string;
  pssCode: string;
  submittedScheduleMw: number;
  actualInjectionMw: number;
};
type ManagedQca = { code: string; name: string };
type ManagedPss = {
  code: string;
  name: string;
  qcaCode: string;
  qcaName: string;
  installedCapacityMw: number;
};
type WeatherOverlay = "radar" | "satellite" | "wind" | "rain" | "waves";
type ChartMode = "time" | "block";
type ChartKey = "schedule" | "wind" | "solar" | "deviation";
type GenerationMixItem = { name: string; value: number; color: string };
type DashboardQca = {
  qca_id: number;
  code: string;
  name: string;
  address: string;
  state: string;
  contact_person: string;
  mobile: string;
  email: string;
  status: string;
};
type DashboardPss = {
  pss_id: number;
  code: string;
  name: string;
  district: string;
  state: string;
  voltage_kv: number;
  qca_id: number;
  installed_capacity_mw: number;
  status: string;
};
type DashboardData = {
  qca: DashboardQca[];
  pss: DashboardPss[];
};

const deviationCalloutGroups = new WeakMap<
  Highcharts.Chart,
  Highcharts.SVGElement
>();

function renderDeviationBandCallouts(chart: Highcharts.Chart): void {
  deviationCalloutGroups.get(chart)?.destroy();

  const axis = chart.yAxis[1];
  if (!axis) {
    return;
  }

  const { min, max } = axis.getExtremes();
  const group = chart.renderer
    .g("deviation-band-callouts")
    .attr({ zIndex: 8 })
    .add();
  deviationCalloutGroups.set(chart, group);

  const boundaries = [max, 35, 25, 15, -15, -25, -35, min];
  const bands = [
    { upper: max, lower: 35, range: "> +35%", name: "Band 4", color: "#ff1744" },
    { upper: 35, lower: 25, range: "> +25% to +35%", name: "Band 3", color: "#ff5a1f" },
    { upper: 25, lower: 15, range: "> +15% to +25%", name: "Band 2", color: "#f5b400" },
    { upper: 15, lower: -15, range: "+15% to -15%", name: "Band 1", color: "#00c978" },
    { upper: -15, lower: -25, range: "> -15% to -25%", name: "Band 2", color: "#f5b400" },
    { upper: -25, lower: -35, range: "> -25% to -35%", name: "Band 3", color: "#ff5a1f" },
    { upper: -35, lower: min, range: "< -35%", name: "Band 4", color: "#ff1744" },
  ];

  const plotRight = chart.plotLeft + chart.plotWidth;
  const connectorStart = plotRight + 42;
  const bracketX = plotRight + 70;
  const boxX = plotRight + 80;
  const boxWidth = 91;
  const boxHeight = 23;

  boundaries.forEach((value, index) => {
    const y = Math.round(axis.toPixels(value));
    chart.renderer
      .path([["M", connectorStart, y], ["L", bracketX, y]])
      .attr({
        stroke: bands[Math.min(index, bands.length - 1)].color,
        "stroke-width": 1,
      })
      .add(group);
  });

  bands.forEach((band) => {
    const upperY = Math.round(axis.toPixels(band.upper));
    const lowerY = Math.round(axis.toPixels(band.lower));
    const centerY = Math.round((upperY + lowerY) / 2);
    const boxY = centerY - boxHeight / 2;

    chart.renderer
      .path([
        ["M", bracketX, upperY],
        ["L", bracketX, lowerY],
        ["M", bracketX, centerY],
        ["L", boxX, centerY],
      ])
      .attr({ stroke: band.color, "stroke-width": 1 })
      .add(group);

    chart.renderer
      .rect(boxX, boxY, boxWidth, boxHeight, 3)
      .attr({
        fill: "#ffffff",
        stroke: band.color,
        "stroke-width": 1,
      })
      .addClass("deviation-band-callout-box")
      .add(group);

    chart.renderer
      .text(band.range, boxX + boxWidth / 2, boxY + 9)
      .attr({ align: "center" })
      .css({
        color: "#172033",
        fontSize: "7px",
        fontWeight: "500",
      })
      .addClass("deviation-band-callout-text")
      .add(group);
    chart.renderer
      .text(band.name, boxX + boxWidth / 2, boxY + 18)
      .attr({ align: "center" })
      .css({
        color: "#111827",
        fontSize: "7px",
        fontWeight: "700",
      })
      .addClass("deviation-band-callout-text")
      .add(group);
  });
}

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dsmBlockService = inject(DsmBlockService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);

  navigateToMaster(path: "/master/pss" | "/master/qca"): void {
    void this.router.navigateByUrl(path);
  }

  navigateToPssDashboard(pssCode: string): void {
    void this.router.navigate(["/pss-dashboard", pssCode]);
  }

  navigateToQcaDashboard(qcaCode: string, pssCode: string): void {
    void this.router.navigate(["/qca-dashboard"], {
      queryParams: { qca: qcaCode, pss: pssCode },
    });
  }
  private readonly hiddenScheduleSeries = new Set<string>();

  @ViewChild("scheduleChart")
  private scheduleChartElement?: ElementRef<HTMLElement>;
  @ViewChild("generationMixChart")
  private generationMixChartElement?: ElementRef<HTMLElement>;
  @ViewChild("deviationChart")
  private deviationChartElement?: ElementRef<HTMLElement>;
  @ViewChild("windChart") private windChartElement?: ElementRef<HTMLElement>;
  @ViewChild("solarChart") private solarChartElement?: ElementRef<HTMLElement>;
  @ViewChild("googleMap") private googleMapElement?: ElementRef<HTMLElement>;

  private readonly dashboardData: DashboardData =
    dashboardDataJson as DashboardData;
  private readonly qcaDashboardScheduleRows = (
    qcaDashboardDataJson as { scheduleAvcRows: QcaDashboardScheduleRow[] }
  ).scheduleAvcRows;
  private readonly managedQcas = (
    qcaManagementDataJson as { rows: ManagedQca[] }
  ).rows;
  private readonly managedPss = (
    pssManagementDataJson as { rows: ManagedPss[] }
  ).rows;

  readonly schedulePeriods = ["Today", "Yesterday", "Weekday"];
  readonly qcaFilters: string[];
  readonly pssFilters: string[];
  selectedSchedulePeriod = this.schedulePeriods[0];
  selectedScheduleDate = this.localDate();
  selectedQca = "All QCAs";
  selectedPss = "All PSS";
  readonly xAxisModes: Array<{ id: ChartMode; label: string }> = [
    { id: "time", label: "Time-wise" },
    { id: "block", label: "Block-wise" },
  ];
  selectedXAxisModes: Record<ChartKey, ChartMode> = {
    schedule: "time",
    wind: "time",
    solar: "time",
    deviation: "time",
  };
  get currentDsmBlock(): number {
    return this.dsmBlockService.currentBlockNumber();
  }

  get currentDsmBlockWindow(): string {
    return this.dsmBlockService.currentBlockWindow();
  }

  generationTotalMw = 0;
  googleMapsError = false;
  generationMixLegend: GenerationMixItem[] = [];
  private dataUpdateTimer?: ReturnType<typeof setInterval>;
  private scheduleChart?: Highcharts.Chart;
  private deviationChart?: Highcharts.Chart;
  private windChart?: Highcharts.Chart;
  private solarChart?: Highcharts.Chart;
  private googleMap?: any;
  private readonly mapThemeEffect = effect(() => {
    const isDark = this.themeService.isDarkMode();
    const styles = this.googleMapStyles(isDark);
    this.googleMap?.setOptions({ styles });
    this.applyMapDataTheme(this.googleMap, isDark);
  });
  private googleMapsApi?: any;
  private maharashtraBounds?: any;
  private mapResizeObserver?: ResizeObserver;
  private fullscreenFitTimer?: ReturnType<typeof setTimeout>;
  private readonly onFullscreenChange = (): void => {
    // A fullscreen transition does not reliably emit a window resize event.
    setTimeout(() => this.reflowCharts());
  };
  private readonly onMapFullscreenChange = (): void => {
    if (this.fullscreenFitTimer) {
      clearTimeout(this.fullscreenFitTimer);
    }
    this.fullscreenFitTimer = setTimeout(() => {
      const fullscreenElement = document.fullscreenElement;
      const isPrimaryMapFullscreen = this.isMapFullscreen(
        this.googleMapElement?.nativeElement,
        fullscreenElement,
      );
      if (isPrimaryMapFullscreen && this.googleMap && this.googleMapsApi) {
        this.googleMapsApi.event.addListenerOnce(this.googleMap, "idle", () => {
          this.googleMap.setZoom(this.googleMap.getZoom() + 0.8);
        });
        this.fitMaharashtraToCard();
      }
    }, 200);
  };

  kpis: Kpi[] = [];
  entityCards: EntityCard[] = [];
  alerts: Alert[] = [];
  activities: Activity[] = [];
  deviations: Deviation[] = [];
  topPssDeviations: TopPssDeviation[] = [];
  readonly weatherImpactedPss = weatherImpactedPss;
  private readonly weatherPssLocations: Record<
    string,
    { lat: number; lng: number }
  > = {
    "satara-pss": { lat: 17.6805, lng: 73.9953 },
    "dhule-pss": { lat: 20.9042, lng: 74.7749 },
    "solapur-pss": { lat: 17.6599, lng: 75.9064 },
    "sangli-pss": { lat: 16.8524, lng: 74.5815 },
    "nashik-pss": { lat: 19.9975, lng: 73.7898 },
    "pune-pss": { lat: 18.5204, lng: 73.8567 },
    "kolhapur-pss": { lat: 16.705, lng: 74.2433 },
    "nagpur-pss": { lat: 21.1458, lng: 79.0882 },
    "aurangabad-pss": { lat: 19.8762, lng: 75.3433 },
    "mumbai-pss": { lat: 19.076, lng: 72.8777 },
  };

  constructor() {
    this.qcaFilters = [
      "All QCAs",
      ...this.dashboardData.qca.map((item) => item.code),
    ];
    this.pssFilters = [
      "All PSS",
      ...this.dashboardData.pss.map((item) => item.code),
    ];
  }

  ngOnInit(): void {
    this.loadDashboardSnapshot();
  }

  ngAfterViewInit(): void {
    this.createCharts();
    setTimeout(() => this.createResourceCharts());
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    void this.initializeGoogleMap();
    this.dataUpdateTimer = setInterval(
      () => this.refreshDashboardData(),
      15000,
    );
  }

  private async initializeGoogleMap(): Promise<void> {
    const apiKey = (
      environment as typeof environment & {
        googleMapsKey?: string;
      }
    ).googleMapsKey;

    if (!apiKey || !this.googleMapElement || typeof document === "undefined") {
      this.googleMapsError = true;
      return;
    }

    try {
      // Load Google Maps
      await this.loadGoogleMaps(apiKey);

      const googleMaps = (
        window as unknown as {
          google: {
            maps: any;
          };
        }
      ).google.maps;

      // =====================================================
      // CREATE MAP
      // =====================================================
      const map = new googleMaps.Map(this.googleMapElement.nativeElement, {
        center: {
          lat: 19.7515,
          lng: 75.7139,
        },

        zoom: 6,

        styles: this.googleMapStyles(this.themeService.isDarkMode()),

        mapTypeControl: true,

        mapTypeControlOptions: {
          position: googleMaps.ControlPosition.TOP_RIGHT,
        },

        zoomControl: true,

        zoomControlOptions: {
          position: googleMaps.ControlPosition.LEFT_TOP,
        },

        streetViewControl: false,

        fullscreenControl: true,

        fullscreenControlOptions: {
          position: googleMaps.ControlPosition.RIGHT_TOP,
        },
      });

      this.googleMap = map;
      this.googleMapsApi = googleMaps;
      this.mapResizeObserver = new ResizeObserver(() =>
        this.fitMaharashtraToCard(),
      );
      this.mapResizeObserver.observe(this.googleMapElement.nativeElement);
      document.addEventListener("fullscreenchange", this.onMapFullscreenChange);

      // =====================================================
      // HIGHLIGHT MAHARASHTRA
      // =====================================================
      this.highlightMaharashtra(googleMaps, map);

      // =====================================================
      // RENEWABLE ENERGY ASSETS
      // =====================================================
      const assetMarkerColors = {
        solar: "#fbbf24",
        wind: "#22d3ee",
      } as const;

      const assets = [
        {
          position: { lat: 19.9975, lng: 73.7898 },
          title: "Nashik Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 19.8762, lng: 75.3433 },
          title: "Chhatrapati Sambhajinagar Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 19.076, lng: 72.8777 },
          title: "Mumbai Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 18.6298, lng: 73.7997 },
          title: "Baramati Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 19.813, lng: 75.202 },
          title: "Chhatrapati Sambhajinagar Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 18.4088, lng: 76.5604 },
          title: "Solapur Wind Farm",
          color: "#1877e6",
        },

        {
          position: { lat: 21.1458, lng: 79.0882 },
          title: "Nagpur Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 20.9374, lng: 77.7796 },
          title: "Amravati Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 20.7059, lng: 77.0219 },
          title: "Akola Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 20.9042, lng: 74.7749 },
          title: "Dhule Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 21.0077, lng: 75.5626 },
          title: "Jalgaon Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 19.0946, lng: 74.7384 },
          title: "Ahmednagar Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 17.6599, lng: 75.9064 },
          title: "Solapur Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 16.8524, lng: 74.5815 },
          title: "Kolhapur Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 17.6805, lng: 74.0183 },
          title: "Satara Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 18.5204, lng: 73.8567 },
          title: "Pune Solar Park",
          color: "#22ad63",
        },
        {
          position: { lat: 19.2183, lng: 73.0868 },
          title: "Kalyan Wind Farm",
          color: "#1877e6",
        },
        {
          position: { lat: 19.2183, lng: 72.9781 },
          title: "Thane Solar Park",
          color: "#22ad63",
        },

        {
          position: { lat: 19.1383, lng: 77.321 },
          title: "Nanded Solar Park",
          color: "#22ad63",
        },
      ];

      // =====================================================
      // ADD MARKERS
      // =====================================================
      assets.forEach((asset) => {
        new googleMaps.Marker({
          position: asset.position,

          title: asset.title,

          map: map,

          icon: {
            path: googleMaps.SymbolPath.CIRCLE,

            fillColor: asset.title.includes("Solar")
              ? assetMarkerColors.solar
              : assetMarkerColors.wind,

            fillOpacity: 1,

            strokeColor: "#ffffff",

            strokeWeight: 2.5,

            scale: 6,
          },
        });
      });

      this.addWeatherImpactMarkers(googleMaps, map);
    } catch (error) {
      console.error("Google Maps initialization failed:", error);

      this.googleMapsError = true;
    }
  }

  private addWeatherImpactMarkers(googleMaps: any, map: any): void {
    this.weatherImpactedPss.forEach((pss) => {
      const location = this.weatherPssLocations[pss.id];
      if (!location) return;
      const marker = new googleMaps.Marker({
        position: location,
        title: `${pss.name} — ${pss.alert} alert`,
        map,
        icon: {
          url: this.weatherIconData(pss.weather),
          scaledSize: new googleMaps.Size(28, 28),
          anchor: new googleMaps.Point(14, 14),
        },
        optimized: false,
      });
      marker.addListener(
        "click",
        () => void this.router.navigate(["/weather-dashboard", pss.id]),
      );
    });
  }

  private readonly weatherIconCache = new Map<string, string>();

  weatherIconData(condition: string): string {
    const cached = this.weatherIconCache.get(condition);
    if (cached) return cached;

    const iconArtwork: Record<
      string,
      { from: string; to: string; artwork: string }
    > = {
      Sunny: {
        from: "#f59e0b",
        to: "#facc15",
        artwork: `<g stroke="#fff7c2" stroke-width="2" stroke-linecap="round"><path d="M24 9v4M24 35v4M9 24h4M35 24h4M13.4 13.4l2.8 2.8M31.8 31.8l2.8 2.8M34.6 13.4l-2.8 2.8M16.2 31.8l-2.8 2.8"/><circle cx="24" cy="24" r="8" fill="#ffe45c" stroke="#fff" stroke-width="1.4"/></g>`,
      },
      "Partly Cloudy": {
        from: "#e88b0c",
        to: "#fbbf24",
        artwork: `<circle cx="29" cy="18" r="7" fill="#ffe55c" stroke="#fff6bf" stroke-width="1.5"/><g stroke="#fff6bf" stroke-width="1.5" stroke-linecap="round"><path d="M29 8v3M38 18h-3M35.5 11.5l-2.2 2.2"/></g><path d="M12 32h23a5 5 0 0 0 0-10 8.5 8.5 0 0 0-16-1.3A6 6 0 0 0 12 32Z" fill="url(#cloud)" stroke="#fff" stroke-width="1.5"/>`,
      },
      Cloudy: {
        from: "#64748b",
        to: "#94a3b8",
        artwork: `<path d="M9 31h28a5.5 5.5 0 0 0 0-11 9.5 9.5 0 0 0-18-1.2A6.5 6.5 0 0 0 9 31Z" fill="url(#cloud)" stroke="#fff" stroke-width="1.6"/><path d="M15 35h19" stroke="#d9e4ee" stroke-width="2" stroke-linecap="round" opacity=".85"/>`,
      },
      "Heavy Wind": {
        from: "#008b8b",
        to: "#14b8a6",
        artwork: `<g fill="none" stroke="#d5fffb" stroke-width="2.4" stroke-linecap="round"><path d="M9 18h21c5 0 5-7 1-8-3-.8-5 1-5.8 3"/><path d="M8 25h28c5 0 5 7 0 8-3 .6-5-1-5.8-3"/><path d="M12 32h12"/></g>`,
      },
      Thunderstorm: {
        from: "#6d28d9",
        to: "#a855f7",
        artwork: `<path d="M10 27h27a5.5 5.5 0 0 0 0-11 9 9 0 0 0-17-1.3A6.8 6.8 0 0 0 10 27Z" fill="url(#cloud)" stroke="#fff" stroke-width="1.5"/><path d="m24 26-5 9h5l-2 7 9-11h-5l3-5Z" fill="#ffe45e" stroke="#fff7b2" stroke-width=".8"/><g stroke="#8ee7ff" stroke-width="2" stroke-linecap="round"><path d="M14 31l-1.5 3M35 31l-1.5 3"/></g>`,
      },
      "Light Rain": {
        from: "#0284c7",
        to: "#38bdf8",
        artwork: `<path d="M10 26h27a5.5 5.5 0 0 0 0-11 9 9 0 0 0-17-1.2A6.8 6.8 0 0 0 10 26Z" fill="url(#cloud)" stroke="#fff" stroke-width="1.5"/><g stroke="#bff3ff" stroke-width="2.2" stroke-linecap="round"><path d="M16 31l-2 4M25 31l-2 4M34 31l-2 4"/></g>`,
      },
      "Heavy Rain": {
        from: "#0369a1",
        to: "#06b6d4",
        artwork: `<path d="M9 25h29a5.8 5.8 0 0 0 0-11.5 9.5 9.5 0 0 0-18-1.2A7 7 0 0 0 9 25Z" fill="url(#cloud)" stroke="#fff" stroke-width="1.5"/><g stroke="#a5f3fc" stroke-width="2.5" stroke-linecap="round"><path d="M14 30l-2.5 5M23 29l-3 7M32 30l-2.5 5M39 29l-2 4"/></g>`,
      },
    };

    const icon = iconArtwork[condition] ?? iconArtwork["Cloudy"];
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><defs><linearGradient id="bg" x1="7" y1="5" x2="41" y2="43"><stop stop-color="${icon.from}"/><stop offset="1" stop-color="${icon.to}"/></linearGradient><linearGradient id="cloud" x1="15" y1="15" x2="30" y2="34"><stop stop-color="#f8fbff"/><stop offset="1" stop-color="#a9bfd1"/></linearGradient><filter id="shadow" x="-30%" y="-30%" width="160%" height="170%"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#071827" flood-opacity=".5"/></filter></defs><circle cx="24" cy="24" r="21" fill="url(#bg)" stroke="#fff" stroke-width="2"/><g filter="url(#shadow)">${icon.artwork}</g></svg>`;
    const dataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    this.weatherIconCache.set(condition, dataUrl);
    return dataUrl;
  }

  private isMapFullscreen(
    mapElement: HTMLElement | undefined,
    fullscreenElement: Element | null,
  ): boolean {
    return (
      !!mapElement &&
      !!fullscreenElement &&
      (mapElement === fullscreenElement ||
        mapElement.contains(fullscreenElement) ||
        fullscreenElement.contains(mapElement))
    );
  }

  private googleMapStyles(isDark: boolean): any[] {
    if (!isDark) return [];

    return [
      { elementType: "geometry", stylers: [{ color: "#173047" }] },
      { elementType: "labels.text.fill", stylers: [{ color: "#dce8f1" }] },
      {
        elementType: "labels.text.stroke",
        stylers: [{ color: "#10263a" }, { weight: 2 }],
      },
      {
        featureType: "administrative",
        elementType: "geometry.stroke",
        stylers: [{ color: "#4d6a78" }],
      },
      {
        featureType: "administrative.province",
        elementType: "geometry.stroke",
        stylers: [{ color: "#41616d" }],
      },
      {
        featureType: "administrative.locality",
        elementType: "labels.text.fill",
        stylers: [{ color: "#e7f0f5" }],
      },
      {
        featureType: "landscape.natural",
        elementType: "geometry",
        stylers: [{ color: "#214c48" }],
      },
      {
        featureType: "poi",
        elementType: "geometry",
        stylers: [{ color: "#1b3d48" }],
      },
      {
        featureType: "poi",
        elementType: "labels.text.fill",
        stylers: [{ color: "#a8c5bd" }],
      },
      {
        featureType: "poi.park",
        elementType: "geometry",
        stylers: [{ color: "#1b5144" }],
      },
      {
        featureType: "poi.park",
        elementType: "labels.text.fill",
        stylers: [{ color: "#b1d7c1" }],
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#315666" }],
      },
      {
        featureType: "road",
        elementType: "geometry.stroke",
        stylers: [{ color: "#1c3c4d" }],
      },
      {
        featureType: "road",
        elementType: "labels.text.fill",
        stylers: [{ color: "#cbdbe5" }],
      },
      {
        featureType: "road.highway",
        elementType: "geometry",
        stylers: [{ color: "#527d89" }],
      },
      {
        featureType: "transit",
        elementType: "geometry",
        stylers: [{ color: "#2b5363" }],
      },
      {
        featureType: "water",
        elementType: "geometry",
        stylers: [{ color: "#0f2a40" }],
      },
      {
        featureType: "water",
        elementType: "labels.text.fill",
        stylers: [{ color: "#83bdd3" }],
      },
    ];
  }

  private applyMapDataTheme(map: any, isDark: boolean): void {
    if (!map?.data) return;

    map.data.setStyle((feature: any) => {
      if (feature.getProperty("isOutsideMask")) {
        return {
          fillColor: isDark ? "#10263a" : "#e5e7eb",
          fillOpacity: 1,
          strokeOpacity: 0,
          clickable: false,
        };
      }
      return {
        fillOpacity: 0,
        strokeColor: isDark ? "#ff6d7a" : "#ef4444",
        strokeOpacity: 1,
        strokeWeight: isDark ? 1.6 : 1,
        clickable: false,
      };
    });
  }

  private highlightMaharashtra(googleMaps: any, map: any): void {
    fetch("assets/geojson/maharashtra.json")
      .then((res) => res.json())
      .then((topology: any) => {
        console.log("Maharashtra Topology:", topology);

        const object = topology.objects["maharashtra-divisions"];

        if (!object) {
          console.error("maharashtra-divisions object not found");
          return;
        }

        // ONLY OUTER MAHARASHTRA BOUNDARY
        const boundary = topojson.mesh(
          topology,
          object,
          (a: any, b: any) => a === b,
        );
        const state = topojson.merge(topology, object.geometries) as any;
        const polygons =
          state.type === "Polygon" ? [state.coordinates] : state.coordinates;
        const holes = polygons.map((polygon: number[][][]) => {
          const ring = polygon[0];
          const signedArea = ring.reduce(
            (area: number, point: number[], index: number) => {
              const next = ring[(index + 1) % ring.length];
              return area + point[0] * next[1] - next[0] * point[1];
            },
            0,
          );
          return signedArea > 0 ? [...ring].reverse() : ring;
        });
        const stateBounds = new googleMaps.LatLngBounds();
        holes.forEach((ring: number[][]) => {
          ring.forEach(([lng, lat]) => stateBounds.extend({ lat, lng }));
        });

        if (map === this.googleMap) {
          this.maharashtraBounds = stateBounds;
          this.fitMaharashtraToCard();
        }
        map.setOptions({
          restriction: {
            latLngBounds: stateBounds,
            // Keep the initial focus on Maharashtra without blocking user zoom.
            strictBounds: false,
          },
          minZoom: 6,
          maxZoom: 15,
        });

        // Clear previously loaded features
        map.data.forEach((feature: any) => {
          map.data.remove(feature);
        });

        // Shade the map outside Maharashtra. Maharashtra coordinates are holes,
        // so its normal Google map colours remain visible.
        map.data.addGeoJson({
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: { isOutsideMask: true },
              geometry: {
                type: "Polygon",
                coordinates: [
                  [
                    [55, 5],
                    [100, 5],
                    [100, 38],
                    [55, 38],
                    [55, 5],
                  ],
                  ...holes,
                ],
              },
            },
            {
              type: "Feature",
              properties: {
                name: "Maharashtra",
              },
              geometry: boundary,
            },
          ],
        });

        this.applyMapDataTheme(map, this.themeService.isDarkMode());
      })
      .catch((error) => {
        console.error("Failed to load Maharashtra GeoJSON:", error);
      });
  }

  private drawTopoJsonGeometry(
    googleMaps: any,
    map: any,
    geometry: any,
    arcs: any[],
  ): void {
    if (geometry.type === "GeometryCollection") {
      geometry.geometries.forEach((item: any) => {
        this.drawTopoJsonGeometry(googleMaps, map, item, arcs);
      });

      return;
    }

    if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") {
      return;
    }

    const polygons =
      geometry.type === "Polygon" ? [geometry.arcs] : geometry.arcs;

    polygons.forEach((polygon: any[]) => {
      polygon.forEach((ring: number[]) => {
        const path: any[] = [];

        ring.forEach((arcIndex: number) => {
          const reverse = arcIndex < 0;

          const index = reverse ? ~arcIndex : arcIndex;

          const arc = arcs[index];

          const points = reverse ? [...arc].reverse() : arc;

          points.forEach((point: number[]) => {
            path.push({
              lng: point[0],
              lat: point[1],
            });
          });
        });

        this.drawDottedBoundary(googleMaps, map, path);
      });
    });
  }

  private drawDottedBoundary(googleMaps: any, map: any, path: any[]): void {
    new googleMaps.maps.Polyline({
      map: map,

      path: path,

      strokeOpacity: 0,

      icons: [
        {
          icon: {
            path: googleMaps.maps.SymbolPath.CIRCLE,

            scale: 1.5,

            fillColor: "#ef4444",
            fillOpacity: 1,

            strokeColor: "#ef4444",
            strokeOpacity: 1,

            strokeWeight: 0,
          },

          offset: "0",
          repeat: "7px",
        },
      ],

      clickable: false,

      zIndex: 1000,
    });
  }

  // =========================================================
  // LOAD GOOGLE MAPS
  // =========================================================
  private loadGoogleMaps(apiKey: string): Promise<void> {
    if (
      (
        window as Window & {
          google?: unknown;
        }
      ).google
    ) {
      return Promise.resolve();
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      "script[data-google-maps]",
    );

    if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener("load", () => resolve(), {
          once: true,
        });

        existingScript.addEventListener(
          "error",
          () => reject(new Error("Google Maps script failed to load")),
          { once: true },
        );
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");

      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        apiKey,
      )}`;

      script.async = true;

      script.dataset["googleMaps"] = "true";

      script.onload = () => resolve();

      script.onerror = () =>
        reject(new Error("Google Maps script failed to load"));

      document.head.appendChild(script);
    });
  }

  ngOnDestroy(): void {
    this.mapResizeObserver?.disconnect();
    document.removeEventListener(
      "fullscreenchange",
      this.onMapFullscreenChange,
    );
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    if (this.fullscreenFitTimer) {
      clearTimeout(this.fullscreenFitTimer);
    }
    if (this.dataUpdateTimer) {
      clearInterval(this.dataUpdateTimer);
    }
    this.scheduleChart?.destroy();
    this.deviationChart?.destroy();
    this.windChart?.destroy();
    this.solarChart?.destroy();
  }

  private fitMaharashtraToCard(): void {
    if (!this.googleMap || !this.googleMapsApi || !this.maharashtraBounds) {
      return;
    }

    requestAnimationFrame(() => {
      this.googleMapsApi.event.trigger(this.googleMap, "resize");
      this.googleMap.fitBounds(this.maharashtraBounds, 0);
    });
  }

  private createCharts(): void {
    const common: Highcharts.Options = {
      credits: { enabled: false },
      accessibility: { enabled: false },
      title: { text: undefined },
      legend: { enabled: false },
      chart: {
        backgroundColor: "transparent",
        animation: false,
        style: { fontFamily: "var(--app-font-sans)" },
      },
    };
    const categories = Array.from({ length: 96 }, (_, index) =>
      String(index + 1),
    );
    this.scheduleChart = Highcharts.chart(
      this.scheduleChartElement!.nativeElement,
      {
        ...common,
        chart: {
          ...common.chart,
          type: "spline",
          margin: [12, 12, 38, 48],
          zoomType: "x",
        },
        xAxis: this.scheduleXAxisConfig(
          categories,
          this.selectedXAxisModes.schedule,
        ),
        yAxis: {
          min: 0,
          // Highcharts derives the maximum from the live Schedule/Actual/Day
          // Ahead values each time the chart data is refreshed.
          tickPixelInterval: 45,
          title: {
            text: "MW",
            align: "high",
            rotation: 0,
            y: -10,
            x: -28,
            style: { color: "#475569", fontSize: "10px" },
          },
          gridLineColor: "#e9eef4",
          labels: {
            align: "right",
            x: -8,
            reserveSpace: true,
            format: "{value:,.2f}",
            style: { color: "#64748b", fontSize: "10px" },
          },
        },
        tooltip: this.generationTooltip(this.selectedXAxisModes.schedule),
        plotOptions: { series: { events: { legendItemClick: () => false } } },
        series: this.scheduleSeries() as Highcharts.SeriesOptionsType[],
      },
    );

    const deviationSeries = this.deviationSeries();
    const deviationPercentageTicks =
      this.deviationPercentageTicks(deviationSeries);
    const deviationPercentageRange = deviationPercentageTicks.at(-1) ?? 35;
    this.deviationChart = Highcharts.chart(
      this.deviationChartElement!.nativeElement,
      {
        ...common,
        chart: {
          ...common.chart,
          margin: [8, 180, 72, 112],
          zoomType: "x",
          plotBorderWidth: 0,
          events: {
            render: function () {
              renderDeviationBandCallouts(this);
            },
          },
        },
        xAxis: this.deviationXAxisConfig(
          Array.from({ length: 96 }, (_, index) => String(index + 1)),
          this.selectedXAxisModes.deviation,
        ),
        yAxis: [
          {
            min: -18000,
            max: 18000,
            tickPositions: [-18000, -12000, -6000, 0, 6000, 12000, 18000],
            title: { text: undefined },
            gridLineWidth: 0,
            labels: {
              format: "{value:,.0f}",
              style: { color: "#64748b", fontSize: "9px" },
            },
          },
          {
            min: -deviationPercentageRange,
            max: deviationPercentageRange,
            tickPositions: deviationPercentageTicks,
            opposite: true,
            title: { text: undefined },
            gridLineWidth: 0,
            labels: {
              format: "{value}%",
              style: { color: "#64748b", fontSize: "9px" },
            },
            plotBands: [
              {
                from: -1000,
                to: -35,
                color: "rgba(255, 112, 150, 0.48)",
                className: "deviation-band-critical",
              },
              {
                from: -35,
                to: -25,
                color: "rgba(255, 163, 92, 0.52)",
                className: "deviation-band-warning",
              },
              {
                from: -25,
                to: -15,
                color: "rgba(255, 223, 91, 0.52)",
                className: "deviation-band-caution",
              },
              {
                from: -15,
                to: 15,
                color: "rgba(127, 231, 151, 0.46)",
                className: "deviation-band-safe",
              },
              {
                from: 15,
                to: 25,
                color: "rgba(255, 223, 91, 0.52)",
                className: "deviation-band-caution",
              },
              {
                from: 25,
                to: 35,
                color: "rgba(255, 163, 92, 0.52)",
                className: "deviation-band-warning",
              },
              {
                from: 35,
                to: 1000,
                color: "rgba(255, 112, 150, 0.48)",
                className: "deviation-band-critical",
              },
            ],
            plotLines: [
              {
                value: -35,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-critical",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#f43f5e", fontSize: "13px" },
                },
              },
              {
                value: -25,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-warning",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#f97316", fontSize: "13px" },
                },
              },
              {
                value: -15,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-safe",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#22c55e", fontSize: "13px" },
                },
              },
              {
                value: 0,
                color: "#168a55",
                width: 1.5,
                zIndex: 2,
                className: "deviation-zero-line",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#10b981", fontSize: "13px" },
                },
              },
              {
                value: 15,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-safe",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#22c55e", fontSize: "13px" },
                },
              },
              {
                value: 25,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-warning",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#f59e0b", fontSize: "13px" },
                },
              },
              {
                value: 35,
                color: "transparent",
                width: 0,
                zIndex: 2,
                className: "deviation-limit-critical",
                label: {
                  text: "●",
                  align: "right",
                  x: 7,
                  y: 4,
                  style: { color: "#f43f5e", fontSize: "13px" },
                },
              },
            ],
          },
          {
            opposite: false,
            offset: 48,
            title: { text: undefined },
            gridLineWidth: 0,
            labels: {
              formatter: function () {
                return `₹${Highcharts.numberFormat(Number(this.value) / 1000, 0)}k`;
              },
              style: { color: "#8b5cf6", fontSize: "9px" },
            },
          },
        ],
        tooltip: this.deviationTooltip(this.selectedXAxisModes.deviation),
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
          y: 4,
          padding: 0,
          itemDistance: 12,
          symbolWidth: 14,
          symbolHeight: 9,
          itemStyle: {
            color: "#334155",
            fontSize: "10px",
            fontWeight: "normal",
          },
          itemHiddenStyle: { color: "#94a3b8" },
        },
        plotOptions: {
          column: {
            grouping: false,
            pointPadding: 0.04,
            groupPadding: 0.02,
            borderWidth: 0,
          },
          series: { animation: false, states: { inactive: { opacity: 1 } } },
        },
        series: deviationSeries as Highcharts.SeriesOptionsType[],
      },
    );
  }

  private createResourceCharts(): void {
    const render = (
      host: HTMLElement,
      name: string,
      peak: number,
      mode: ChartMode,
    ): Highcharts.Chart => {
      const isWind = name === "Wind";

      const availableData = Array.from({ length: 96 }, (_, i) => {
        if (isWind) {
          const variation =
            0.08 * Math.sin(i / 2.8) +
            0.06 * Math.sin(i / 6.5) +
            0.04 * Math.cos(i / 11) +
            0.025 * Math.sin(i / 1.7);
          return peak * (0.76 + variation);
        }

        const daylight = Math.max(0, Math.sin(((i - 24) / 48) * Math.PI));
        const solarVariation =
          0.06 * Math.sin(i / 2.5) +
          0.035 * Math.cos(i / 5) +
          0.02 * Math.sin(i / 1.8);
        return peak * Math.max(0, daylight * (0.84 + solarVariation));
      });

      const scheduledData = Array.from({ length: 96 }, (_, i) => {
        if (isWind) {
          const variation =
            0.055 * Math.sin(i / 3.5) +
            0.035 * Math.cos(i / 7) +
            0.02 * Math.sin(i / 1.9);
          return peak * (0.62 + variation);
        }
        const daylight = Math.max(0, Math.sin(((i - 24) / 48) * Math.PI));
        const scheduleVariation =
          0.04 * Math.sin(i / 3) +
          0.025 * Math.cos(i / 6) +
          0.015 * Math.sin(i / 2);
        return peak * Math.max(0, daylight * (0.7 + scheduleVariation));
      });

      const actualData = Array.from({ length: 96 }, (_, i) => {
        if (isWind) {
          const windVariation =
            0.1 * Math.sin(i / 2.5) +
            0.07 * Math.cos(i / 5) +
            0.04 * Math.sin(i / 9);
          return Math.max(0, scheduledData[i] * (1 + windVariation));
        }
        const daylight = Math.max(0, Math.sin(((i - 26) / 48) * Math.PI));
        const actualVariation =
          0.06 * Math.sin(i / 2.5) +
          0.03 * Math.cos(i / 5) +
          0.02 * Math.sin(i / 1.8);
        return Math.max(0, peak * daylight * (0.65 + actualVariation));
      });

      const deviationData = actualData.map((actual, i) => {
        const scheduled = scheduledData[i];
        if (!scheduled) {
          return 0;
        }
        return ((actual - scheduled) / scheduled) * 100;
      });

      const blocks = Array.from({ length: 96 }, (_, i) => String(i + 1));

      return Highcharts.chart(host, {
        chart: {
          type: "spline",
          backgroundColor: "transparent",
          animation: false,
          zoomType: "x",
        },
        title: { text: undefined },
        credits: { enabled: false },
        xAxis: this.resourceChartXAxisConfig(blocks, mode),
        yAxis: [
          {
            title: { text: "MW" },
            min: 0,
          },
          {
            opposite: true,
            min: -20,
            max: 20,
            title: { text: "Deviation (%)" },
            labels: { format: "{value}%" },
            plotLines: [{ value: 0, width: 1, color: "#64748b", zIndex: 5 }],
          },
        ],
        tooltip: this.generationTooltip(mode),
        series: [
          {
            type: "spline",
            name: "Available Capacity (MW)",
            data: availableData,
            color: "#b42bd4",
            lineWidth: 2,
            marker: { enabled: false },
          },
          {
            type: "spline",
            name: "Scheduled (MW)",
            data: scheduledData,
            color: "#2584e8",
            lineWidth: 2,
            marker: { enabled: false },
          },
          {
            type: "spline",
            name: "Actual (MW)",
            data: this.visibleThroughPreviousBlock(actualData),
            color: "#63b832",
            lineWidth: 2,
            marker: { enabled: false },
          },
          {
            type: "column",
            name: "Deviation (%)",
            yAxis: 1,
            data: this.visibleThroughPreviousBlock(deviationData),
            color: "#ff4141",
            borderWidth: 0,
            pointPadding: 0.05,
            groupPadding: 0.05,
          },
        ],
      });
    };

    if (this.windChartElement) {
      this.windChart = render(
        this.windChartElement.nativeElement,
        "Wind",
        16000,
        this.selectedXAxisModes.wind,
      );
    }

    if (this.solarChartElement) {
      this.solarChart = render(
        this.solarChartElement.nativeElement,
        "Solar",
        9000,
        this.selectedXAxisModes.solar,
      );
    }
  }

  refreshDashboardData(): void {
    this.loadDashboardSnapshot();
    const scheduleSeries = this.scheduleSeries();
    this.scheduleChart?.series.forEach((series, index) => {
      series.setData(
        (scheduleSeries[index] as { data: Array<number | null> }).data,
        false,
      );
    });
    this.scheduleChart?.redraw();

    const deviationSeries = this.deviationSeries();
    const deviationPercentageTicks =
      this.deviationPercentageTicks(deviationSeries);
    const deviationPercentageRange = deviationPercentageTicks.at(-1) ?? 35;
    this.deviationChart?.yAxis[1].update(
      {
        min: -deviationPercentageRange,
        max: deviationPercentageRange,
        tickPositions: deviationPercentageTicks,
      },
      false,
    );
    this.deviationChart?.series.forEach((series, index) => {
      series.setData(
        (deviationSeries[index] as { data: Array<number | null> }).data,
        false,
      );
    });
    this.deviationChart?.redraw();
  }

  onSchedulePeriodChange(period: string): void {
    this.selectedSchedulePeriod = period;
    this.refreshDashboardData();
  }

  onScheduleDateChange(date: string): void {
    this.selectedScheduleDate = date;
    this.refreshDashboardData();
  }

  onChartXAxisModeChange(chart: ChartKey, mode: ChartMode): void {
    this.selectedXAxisModes[chart] = mode;
    this.updateXAxisMode(chart);
  }

  toggleFullscreen(element: HTMLElement): void {
    if (document.fullscreenElement === element) {
      void document.exitFullscreen();
      return;
    }
    void element.requestFullscreen?.();
  }

  toggleScheduleSeries(name: string): void {
    const series = this.scheduleChart?.series.find(
      (item) => item.name === name,
    );
    if (!series) return;

    series.setVisible(!series.visible, true);
    if (series.visible) this.hiddenScheduleSeries.delete(name);
    else this.hiddenScheduleSeries.add(name);
  }

  isScheduleSeriesHidden(name: string): boolean {
    return this.hiddenScheduleSeries.has(name);
  }

  resetChartZoom(chart: ChartKey): void {
    this.getChart(chart)?.zoomOut();
  }

  private getChart(chart: ChartKey): Highcharts.Chart | undefined {
    return {
      schedule: this.scheduleChart,
      wind: this.windChart,
      solar: this.solarChart,
      deviation: this.deviationChart,
    }[chart];
  }

  private reflowCharts(): void {
    this.scheduleChart?.reflow();
    this.windChart?.reflow();
    this.solarChart?.reflow();
    this.deviationChart?.reflow();
    if (document.fullscreenElement && this.googleMap && this.googleMapsApi) {
      this.googleMapsApi.event.trigger(this.googleMap, "resize");
      this.fitMaharashtraToCard();
    }
  }

  private updateXAxisMode(chart: ChartKey): void {
    const categories = Array.from({ length: 96 }, (_, index) =>
      String(index + 1),
    );
    if (chart === "schedule" && this.scheduleChart) {
      this.scheduleChart.xAxis[0].update(
        this.scheduleXAxisConfig(categories, this.selectedXAxisModes.schedule),
        false,
      );
      this.scheduleChart.update(
        { tooltip: this.generationTooltip(this.selectedXAxisModes.schedule) },
        false,
      );
      this.scheduleChart.redraw(false);
    }
    if (chart === "wind" && this.windChart) {
      this.windChart.xAxis[0].update(
        this.resourceChartXAxisConfig(categories, this.selectedXAxisModes.wind),
        false,
      );
      this.windChart.update(
        { tooltip: this.generationTooltip(this.selectedXAxisModes.wind) },
        false,
      );
      this.windChart.redraw(false);
    }
    if (chart === "solar" && this.solarChart) {
      this.solarChart.xAxis[0].update(
        this.resourceChartXAxisConfig(
          categories,
          this.selectedXAxisModes.solar,
        ),
        false,
      );
      this.solarChart.update(
        { tooltip: this.generationTooltip(this.selectedXAxisModes.solar) },
        false,
      );
      this.solarChart.redraw(false);
    }
    if (chart === "deviation" && this.deviationChart) {
      const categories = Array.from({ length: 96 }, (_, index) =>
        String(index + 1),
      );
      this.deviationChart.xAxis[0].update(
        this.deviationXAxisConfig(
          categories,
          this.selectedXAxisModes.deviation,
        ),
        false,
      );
      this.deviationChart.update(
        { tooltip: this.deviationTooltip(this.selectedXAxisModes.deviation) },
        false,
      );
      this.deviationChart.redraw(false);
    }
  }

  private deviationXAxisConfig(
    categories: string[],
    mode: ChartMode,
  ): Highcharts.XAxisOptions {
    const config = this.scheduleXAxisConfig(categories, mode);
    if (mode === "time") {
      return config;
    }

    return {
      ...config,
      tickPositions: Array.from(
        { length: categories.length },
        (_, index) => index,
      ),
      labels: {
        ...config.labels,
        allowOverlap: true,
        autoRotation: [],
        rotation: 1,
        y: 10,
        style: { color: "#64748b", fontSize: "8px" },
      },
    };
  }

  private deviationTooltip(mode: ChartMode): Highcharts.TooltipOptions {
    return {
      shared: true,
      formatter: function (this: any) {
        const pointIndex = this.points?.[0]?.point?.index ?? 0;
        const startMinutes = pointIndex * 15;
        const formatTime = (minutes: number): string =>
          `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
        const title =
          mode === "time"
            ? `${formatTime(startMinutes)}–${formatTime(startMinutes + 15)}`
            : `Block ${pointIndex + 1} · ${formatTime(startMinutes)}–${formatTime(startMinutes + 15)}`;
        let content = `<b>${title}</b><br/>`;
        this.points?.forEach((point: any) => {
          const suffix =
            point.series.name === "Deviation (%)"
              ? "%"
              : point.series.name === "DSM Amount (₹)"
                ? " ₹"
                : " MW";
          content += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y ?? 0, 2)}${suffix}</b><br/>`;
        });
        return content;
      },
    } as Highcharts.TooltipOptions;
  }

  private generationTooltip(mode: ChartMode): Highcharts.TooltipOptions {
    return {
      shared: true,
      valueDecimals: 2,
      formatter: function (this: any) {
        const pointIndex = this.points?.[0]?.point?.index ?? 0;
        const label =
          mode === "time"
            ? `${String(Math.floor(pointIndex / 4)).padStart(2, "0")}:${String((pointIndex % 4) * 15).padStart(2, "0")}`
            : `Block ${pointIndex + 1}`;
        let html = `<b>${label}</b><br/>`;

        this.points?.forEach((point: any) => {
          const suffix = point.series.name === "Deviation (%)" ? "%" : " MW";
          html += `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y ?? 0, 2)}${suffix}</b><br/>`;
        });
        return html;
      },
    } as Highcharts.TooltipOptions;
  }

  private scheduleXAxisConfig(
    categories: string[],
    mode: ChartMode,
  ): Highcharts.XAxisOptions {
    const isTimeMode = mode === "time";
    return {
      categories,
      min: 0,
      max: categories.length - 1,
      lineColor: "#dce4ee",
      tickLength: 0,
      tickInterval: isTimeMode ? 4 : 1,
      tickPositions: isTimeMode
        ? Array.from({ length: 24 }, (_, index) => index * 4)
        : undefined,
      title: {
        text: isTimeMode ? "Time" : "Block",
        style: { color: "#475569", fontSize: "9px" },
      },
      labels: {
        y: 14,
        autoRotation: [],
        rotation: isTimeMode ? -45 : 1,
        step: 1,
        style: { color: "#64748b", fontSize: isTimeMode ? "9px" : "8px" },
        formatter: function () {
          const pos = this.pos as number;
          if (isTimeMode) {
            if (pos < 0 || pos >= categories.length) {
              return "";
            }
            return `${String(Math.floor(pos / 4)).padStart(2, "0")}:00`;
          }
          return this.value as string;
        },
      },
    };
  }

  private resourceChartXAxisConfig(
    categories: string[],
    mode: ChartMode,
  ): Highcharts.XAxisOptions {
    const isTimeMode = mode === "time";
    return {
      categories,
      tickInterval: isTimeMode ? 4 : 1,
      tickPositions: isTimeMode
        ? Array.from({ length: 24 }, (_, index) => index * 4)
        : Array.from({ length: categories.length }, (_, index) => index),
      title: {
        text: isTimeMode ? "Time" : "Block",
      },
      lineColor: "#dce4ee",
      tickLength: 0,
      labels: {
        y: 14,
        autoRotation: [],
        rotation: isTimeMode ? -45 : 1,
        allowOverlap: !isTimeMode,
        style: { color: "#64748b", fontSize: isTimeMode ? "9px" : "6px" },
        formatter: function () {
          const pos = this.pos as number;
          if (isTimeMode) {
            return pos % 4 === 0
              ? `${String(Math.floor(pos / 4)).padStart(2, "0")}:00`
              : "";
          }
          return this.value as string;
        },
      },
    };
  }

  private selectedQcaItem(): DashboardQca | undefined {
    if (this.selectedQca === "All QCAs") {
      return undefined;
    }
    return this.dashboardData.qca.find(
      (item) => item.code === this.selectedQca,
    );
  }

  private selectedPssItem(): DashboardPss | undefined {
    if (this.selectedPss === "All PSS") {
      return undefined;
    }
    return this.dashboardData.pss.find(
      (item) => item.code === this.selectedPss,
    );
  }

  private getFilteredPssData(): DashboardPss[] {
    const qca = this.selectedQcaItem();
    const pss = this.selectedPssItem();
    let filtered = this.dashboardData.pss;
    if (qca) {
      filtered = filtered.filter((item) => item.qca_id === qca.qca_id);
    }
    if (pss) {
      filtered = filtered.filter((item) => item.code === pss.code);
    }
    return filtered;
  }

  private updatePssFilters(): void {
    const qca = this.selectedQcaItem();
    const filteredPss = qca
      ? this.dashboardData.pss.filter((item) => item.qca_id === qca.qca_id)
      : this.dashboardData.pss;
    const codes = ["All PSS", ...filteredPss.map((item) => item.code)];
    this.pssFilters.splice(0, this.pssFilters.length, ...codes);
  }

  private loadDashboardSnapshot(): void {
    const activeQcas = this.dashboardData.qca.filter(
      (qca) => qca.status.toLowerCase() === "active",
    );
    const inactiveQcas = this.dashboardData.qca.filter(
      (qca) => qca.status.toLowerCase() !== "active",
    );
    const filteredPss = this.getFilteredPssData();
    const activePss = filteredPss.filter(
      (pss) => pss.status.toLowerCase() === "active",
    );
    const inactivePss = filteredPss.filter(
      (pss) => pss.status.toLowerCase() !== "active",
    );
    const totalCapacityMw = filteredPss.reduce(
      (sum, pss) => sum + pss.installed_capacity_mw,
      0,
    );
    const actualGenerationMw = Math.round(
      totalCapacityMw * this.periodActualFactor(),
    );
    const todaysScheduleMw = Math.round(
      totalCapacityMw * this.periodScheduleFactor(),
    );
    const availableCapacityMw = Math.round(
      totalCapacityMw * this.periodAvailableCapacityFactor(),
    );
    const deviationMw = actualGenerationMw - todaysScheduleMw;
    const deviationPercent =
      todaysScheduleMw === 0
        ? 0
        : Number(((deviationMw / todaysScheduleMw) * 100).toFixed(2));
    const receivableDsm = Math.round(Math.max(deviationMw, 0) * 2430);
    const payableDsm = Math.round(Math.max(-deviationMw, 0) * 1620);
    const netDsm = receivableDsm - payableDsm;
    const revenueCr = Number(((totalCapacityMw / 1000) * 1.2).toFixed(2));
    const states = new Set(this.dashboardData.qca.map((qca) => qca.state));

    const mix = filteredPss.reduce(
      (accumulator, pss) => {
        const qca = this.dashboardData.qca.find(
          (item) => item.qca_id === pss.qca_id,
        );
        const technology = qca?.name.toLowerCase() ?? "";
        if (technology.includes("wind")) {
          accumulator.wind += pss.installed_capacity_mw;
        } else if (technology.includes("hybrid")) {
          accumulator.hybrid += pss.installed_capacity_mw;
        } else {
          accumulator.solar += pss.installed_capacity_mw;
        }
        return accumulator;
      },
      { solar: 0, wind: 0, hybrid: 0 },
    );

    this.generationMixLegend = [
      { name: "Solar", value: mix.solar, color: "#f6b20c" },
      { name: "Wind", value: mix.wind, color: "#24a968" },
      { name: "Hybrid", value: mix.hybrid, color: "#2563eb" },
    ];
    this.generationTotalMw = mix.solar + mix.wind + mix.hybrid;

    this.kpis = [
      {
        label: "Installed Cap. (MW)",
        value: "58,000".toLocaleString(),
        detail: ``,
        icon: "⚡",
        tone: "green",
      },
      {
        label: "Schedule (MW)",
        value: totalCapacityMw.toLocaleString(),
        detail: ``,
        icon: "⚡",
        tone: "blue",
      },
      {
        label: "Actual Gen. (MW)",
        value: actualGenerationMw.toLocaleString(),
        detail: ``,
        icon: "▥",
        tone: "green",
      },
      {
        label: "Current Block",
        value: String(this.currentDsmBlock),
        detail: ``,
        icon: "▦",
        tone: "violet",
      },
      {
        label: "Overall Deviation (MW)",
        value: deviationMw.toLocaleString(),
        detail: ``,
        icon: "◕",
        tone: "red",
      },
      {
        label: "Data Availability (%)",
        value: `${Math.max(90, 100 - inactivePss.length * 1.25).toFixed(2)}%`,
        detail: ``,
        icon: "◆",
        tone: "teal",
      },
      {
        label: "Receivable (DSM) (₹)",
        value: receivableDsm.toLocaleString(),
        detail: ``,
        icon: "↳",
        tone: "gold",
      },
      {
        label: "Payable (DSM) (₹)",
        value: payableDsm.toLocaleString(),
        detail: ``,
        icon: "↱",
        tone: "orange",
      },
      {
        label: "Net DSM (₹)",
        value: netDsm.toLocaleString(),
        detail: ``,
        icon: "✦",
        tone: "gold",
      },
    ];

    this.entityCards = [
      {
        title: "REGISTERED QCAs",
        total: this.dashboardData.qca.length.toString(),
        icon: "♧",
        tone: "green",
        rows: [
          {
            label: "Active",
            value: activeQcas.length.toString(),
            tone: "green",
          },
          {
            label: "Inactive",
            value: inactiveQcas.length.toString(),
            tone: "red",
          },
        ],
      },
      {
        title: "GENERATORS",
        total: this.dashboardData.pss.length.toString(),
        icon: "▣",
        tone: "blue",
        rows: [
          {
            label: "Active",
            value: activePss.length.toString(),
            tone: "green",
          },
          {
            label: "Inactive",
            value: inactivePss.length.toString(),
            tone: "red",
          },
        ],
      },
      {
        title: "UTILITIES",
        total: states.size.toString(),
        icon: "♜",
        tone: "violet",
        rows: [
          { label: "Regions", value: states.size.toString(), tone: "green" },
          { label: "PSS", value: activePss.length.toString(), tone: "red" },
        ],
      },
      {
        title: "PENDING APPROVALS",
        total: `${inactiveQcas.length + inactivePss.length}`,
        icon: "▤",
        tone: "orange",
        rows: [
          { label: "QCA", value: inactiveQcas.length.toString() },
          {
            label: "Generators",
            value: inactivePss.length.toString(),
            tone: "green",
          },
          { label: "PSS", value: inactivePss.length.toString(), tone: "red" },
        ],
      },
      {
        title: "REVENUE (This Month)",
        total: `₹${revenueCr.toFixed(2)} Cr`,
        icon: "₹",
        tone: "green",
        rows: [
          {
            label: "DSM Charges",
            value: `₹${(revenueCr * 0.82).toFixed(2)} Cr`,
            tone: "green",
          },
          {
            label: "PSS",
            value: `₹${(revenueCr * 0.18).toFixed(2)} Cr`,
            tone: "green",
          },
        ],
      },
    ];

    this.alerts = [
      {
        level: "critical",
        title: `High deviation detected - ${activePss[0]?.code ?? "PSS-001"}`,
        detail: `${Math.abs(deviationMw).toFixed(2)} MW deviation from schedule`,
        time: "Live",
      },
      {
        level: "critical",
        title: `Schedule deviation exceeded - ${activePss[1]?.code ?? "PSS-002"}`,
        detail:
          "Actual generation is significantly different from scheduled generation",
        time: "2 min ago",
      },
      {
        level: "warning",
        title: `Meter data unavailable - ${inactivePss[0]?.code ?? "PSS-003"}`,
        detail: `${inactivePss[0]?.name ?? "Power Station"} is not currently in service`,
        time: "5 min ago",
      },
      {
        level: "warning",
        title: `Communication failure - ${inactivePss[1]?.code ?? "PSS-004"}`,
        detail: `${inactivePss[1]?.name ?? "Power Station"} data is not being received`,
        time: "8 min ago",
      },
      {
        level: "warning",
        title: `Registration review pending - ${inactiveQcas[0]?.code ?? "QCA-001"}`,
        detail: `${inactiveQcas[0]?.name ?? "QCA"} registration requires review`,
        time: "10 min ago",
      },
      {
        level: "warning",
        title: `QCA registration pending - ${inactiveQcas[1]?.code ?? "QCA-002"}`,
        detail: `${inactiveQcas[1]?.name ?? "QCA"} is marked pending`,
        time: "15 min ago",
      },
      {
        level: "warning",
        title: "Forecast mismatch detected",
        detail: "Generation forecast differs from scheduled capacity",
        time: "18 min ago",
      },
      {
        level: "critical",
        title: "Grid deviation threshold breached",
        detail: `${Math.abs(deviationMw).toFixed(2)} MW deviation detected`,
        time: "22 min ago",
      },
      {
        level: "success",
        title: `DSM payment received - ${activeQcas[0]?.code ?? "QCA-001"}`,
        detail: `Payment of ₹${(revenueCr * 0.07).toFixed(2)} Lakh received`,
        time: "25 min ago",
      },
      {
        level: "success",
        title: "Schedule successfully submitted",
        detail: "Latest generation schedule has been successfully processed",
        time: "30 min ago",
      },
    ];

    this.activities = [
      {
        icon: "▧",
        title: `Schedule revised by ${this.dashboardData.qca[0]?.code ?? "QCA-001"}`,
        detail: this.dashboardData.qca[0]?.name ?? "",
        tone: "blue",
      },
      {
        icon: "◉",
        title: `Meter data validated - ${activePss[0]?.code ?? "PSS-001"}`,
        detail: `${activePss.length} stations synchronized`,
        tone: "slate",
      },
      {
        icon: "▥",
        title: `DSM bill generated - ${this.dashboardData.qca[1]?.code ?? "QCA-002"}`,
        detail: `Capacity ${totalCapacityMw.toLocaleString()} MW`,
        tone: "violet",
      },
      {
        icon: "✓",
        title: `Corpus payment received - ${this.dashboardData.qca[2]?.code ?? "QCA-003"}`,
        detail: `Revenue ${revenueCr.toFixed(2)} Cr`,
        tone: "green",
      },
      {
        icon: "♧",
        title: `New generator registered - ${activePss[2]?.code ?? "PSS-003"}`,
        detail: `${this.dashboardData.pss.length} total generators available`,
        tone: "teal",
      },
      {
        icon: "⌕",
        title: "User login logged in",
        detail: `${this.dashboardData.qca.length} QCAs monitored`,
        tone: "orange",
      },
    ];

    this.deviations = filteredPss.slice(0, 5).map((pss, index) => {
      const qca = this.dashboardData.qca.find(
        (item) => item.qca_id === pss.qca_id,
      );
      const deviationValue = Math.round(
        pss.installed_capacity_mw * (index % 2 === 0 ? 0.18 : -0.14),
      );
      const deviationPercent = Number(
        ((deviationValue / pss.installed_capacity_mw) * 100).toFixed(2),
      );
      return {
        code: pss.code,
        qca: qca?.code ?? "",
        mw: `${deviationValue > 0 ? "+" : ""}${deviationValue}`,
        percent: `${deviationValue > 0 ? "+" : ""}${deviationPercent.toFixed(2)}%`,
        status: deviationValue >= 0 ? "Over Injection" : "Under Injection",
      };
    });

    const selectedQcaCode =
      this.selectedQca === "All QCAs" ? undefined : this.selectedQca;
    const selectedPssCode =
      this.selectedPss === "All PSS" ? undefined : this.selectedPss;
    const scheduleRowsByPssCode = new Map(
      this.qcaDashboardScheduleRows.map((row) => [row.pssCode, row]),
    );
    const qcaNamesByCode = new Map(
      this.managedQcas.map((qca) => [qca.code, qca.name]),
    );

    this.topPssDeviations = this.managedPss
      .filter(
        (pss) =>
          (!selectedQcaCode || pss.qcaCode === selectedQcaCode) &&
          (!selectedPssCode || pss.code === selectedPssCode),
      )
      .map((pss, index) => {
        const liveRow = scheduleRowsByPssCode.get(pss.code);
        const schedule = liveRow
          ? liveRow.submittedScheduleMw
          : Math.round(
              pss.installedCapacityMw * this.periodScheduleFactor() * 100,
            ) / 100;
        const actual = liveRow
          ? liveRow.actualInjectionMw
          : Math.round(
              (schedule +
                pss.installedCapacityMw * (index % 3 === 1 ? 0.016 : -0.02)) *
                100,
            ) / 100;
        const deviation = Number((actual - schedule).toFixed(2));
        const deviationPercent = schedule
          ? Number(((deviation / schedule) * 100).toFixed(2))
          : 0;
        const deviationAmount = Math.round(Math.abs(deviation) * 2430);
        return {
          pssCode: pss.code,
          pssName: pss.name,
          qcaCode: pss.qcaCode,
          qcaName: qcaNamesByCode.get(pss.qcaCode) ?? pss.qcaName,
          schedule,
          actual,
          deviation,
          deviationPercent,
          deviationAmount,
        };
      })
      .sort(
        (left, right) => Math.abs(right.deviation) - Math.abs(left.deviation),
      )
      .slice(0, 10)
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }

  private scheduleSeries(): Highcharts.SeriesOptionsType[] {
    const peak = Math.max(this.generationTotalMw, 12_000);
    const baseline = Array.from({ length: 97 }, (_, index) => {
      const daylight = Math.max(0, Math.sin(((index - 12) / 72) * Math.PI));
      const variation = Math.sin(index * 0.39) * peak * 0.018;
      return Math.round(peak * (0.28 + daylight * 0.78) + variation);
    });
    return [
      {
        type: "spline",
        name: "Scheduled Generation",
        color: "#1684e8",
        lineWidth: 2,
        marker: { enabled: false },
        data: baseline.map((value) =>
          Math.round(value * this.periodScheduleFactor()),
        ),
      },
      {
        type: "spline",
        name: "Actual Generation",
        color: "#6bbd08",
        lineWidth: 2,
        marker: { enabled: false },
        data: this.visibleThroughPreviousBlock(
          baseline.map((value) =>
            Math.round(value * this.periodActualFactor()),
          ),
        ),
      },
      {
        type: "spline",
        name: "Day Ahead Schedule",
        color: "#f97316",
        lineWidth: 2,
        marker: { enabled: false },
        data: baseline.map((value) =>
          Math.round(value * this.periodAvailableCapacityFactor()),
        ),
      },
    ];
  }

  private generationMixData(): Highcharts.PointOptionsObject[] {
    return this.generationMixLegend.map((item) => ({
      name: item.name,
      y: item.value,
      color: item.color,
    }));
  }

  generationMixPercentage(value: number): number {
    return this.generationTotalMw > 0
      ? Math.round((value / this.generationTotalMw) * 100)
      : 0;
  }

  private deviationSeries(): Highcharts.SeriesOptionsType[] {
    // Aggregate RE behavior: mild overnight over-injection, a cloud-driven
    // shortfall around the solar peak, then recovery during the evening.
    const anchors: Array<[number, number]> = [
      [0, 4],
      [7, 8],
      [10, 14],
      [13, 10],
      [19, 15],
      [22, 9],
      [24, 18],
      [31, 7],
      [38, 1],
      [40, 7],
      [43, 0],
      [47, -7],
      [51, -10],
      [55, -14],
      [59, -18],
      [63, -24],
      [65, -32],
      [67, -28],
      [71, -36],
      [75, -45],
      [77, -39],
      [79, -38],
      [83, -26],
      [87, -10],
      [91, -3],
      [94, -1],
      [95, 12],
    ];
    const periodShift =
      this.selectedSchedulePeriod === "Yesterday"
        ? 2.2
        : this.selectedSchedulePeriod === "Weekday"
          ? -1.4
          : 0;
    const entityShift =
      this.selectedPss !== "All PSS"
        ? -2.1
        : this.selectedQca !== "All QCAs"
          ? 1.3
          : 0;

    const deviationPercent = Array.from({ length: 96 }, (_, index) => {
      const nextIndex = anchors.findIndex(([block]) => block >= index);
      const upper = anchors[Math.max(0, nextIndex)];
      const lower = anchors[Math.max(0, nextIndex - 1)];
      const span = Math.max(1, upper[0] - lower[0]);
      const interpolated =
        lower[1] + ((index - lower[0]) / span) * (upper[1] - lower[1]);
      const noise =
        Math.sin(index * 1.73) * 0.9 + Math.cos(index * 0.61) * 0.55;
      return Number(
        (interpolated + noise + periodShift + entityShift).toFixed(2),
      );
    });

    const scheduledMw = Array.from({ length: 96 }, (_, index) => {
      const daylight = Math.max(0, Math.sin(((index - 24) / 48) * Math.PI));
      const windShape =
        0.07 * Math.sin(index / 8) + 0.04 * Math.cos(index / 3.7);
      return Math.max(
        1,
        this.generationTotalMw * (0.52 + daylight * 0.31 + windShape),
      );
    });
    const deviationMw = deviationPercent.map((percent, index) =>
      Math.round((scheduledMw[index] * percent) / 100),
    );
    const dsmAmount = deviationMw.map((value) =>
      Math.round(value * 0.25 * 2430),
    );

    return [
      {
        type: "column",
        name: "Over Injection (MW)",
        color: "#12c76f",
        data: this.visibleThroughCurrentBlock(
          deviationMw.map((value) => Math.max(value, 0)),
        ),
      },
      {
        type: "column",
        name: "Under Injection (MW)",
        color: "#ff1744",
        data: this.visibleThroughCurrentBlock(
          deviationMw.map((value) => Math.min(value, 0)),
        ),
      },
      {
        type: "spline",
        name: "Deviation (%)",
        yAxis: 1,
        color: "#3b35ff",
        lineWidth: 1.5,
        marker: { enabled: false },
        zIndex: 5,
        data: this.visibleThroughCurrentBlock(deviationPercent),
      },
      {
        type: "spline",
        name: "DSM Amount (₹)",
        yAxis: 2,
        color: "#8b5cf6",
        dashStyle: "ShortDash",
        lineWidth: 1.5,
        marker: { enabled: false },
        zIndex: 4,
        data: this.visibleThroughCurrentBlock(dsmAmount),
      },
    ];
  }

  private deviationPercentageTicks(
    series: Highcharts.SeriesOptionsType[],
  ): number[] {
    const deviationData = (series[2] as { data?: number[] }).data ?? [];
    const largestDeviation = deviationData.reduce(
      (largest, value) => Math.max(largest, Math.abs(value)),
      0,
    );
    const extraSteps =
      largestDeviation > 35 ? Math.ceil((largestDeviation - 35) / 15) : 0;
    const maximum = Math.max(50, 35 + extraSteps * 15);
    const positiveTicks = [15, 25, 35];

    for (let tick = 50; tick <= maximum; tick += 15) {
      positiveTicks.push(tick);
    }

    return [
      ...positiveTicks.map((tick) => -tick).reverse(),
      0,
      ...positiveTicks,
    ];
  }

  private periodScheduleFactor(): number {
    if (this.selectedSchedulePeriod === "Yesterday") {
      return 0.84;
    }
    if (this.selectedSchedulePeriod === "Weekday") {
      return 0.9;
    }
    return 0.88;
  }

  private periodActualFactor(): number {
    if (this.selectedSchedulePeriod === "Yesterday") {
      return 0.8;
    }
    if (this.selectedSchedulePeriod === "Weekday") {
      return 0.92;
    }
    return 0.84;
  }

  private periodAvailableCapacityFactor(): number {
    if (this.selectedSchedulePeriod === "Yesterday") {
      return 0.88;
    }
    if (this.selectedSchedulePeriod === "Weekday") {
      return 0.95;
    }
    return 0.92;
  }

  get scheduleLabel(): string {
    return `${this.selectedScheduleDate} (Block-wise)`;
  }

  private visibleChartValues(values: number[]): Array<number | null> {
    const completedBlocks = this.completedBlocksForSelectedDate();
    const visiblePoints = Math.ceil((completedBlocks / 96) * values.length);
    return values.map((value, index) => (index < visiblePoints ? value : null));
  }

  /** Generation is available only through the block before the live one. */
  private visibleThroughPreviousBlock(values: number[]): Array<number | null> {
    const completedBlocks = this.completedBlocksForSelectedDate();
    return values.map((value, index) =>
      index < completedBlocks ? value : null,
    );
  }

  /** Today-only trend values include the live 15-minute block. */
  private visibleThroughCurrentBlock(values: number[]): Array<number | null> {
    return values.map((value, index) =>
      index < this.currentDsmBlock ? value : null,
    );
  }

  private completedBlocksForSelectedDate(): number {
    const today = this.localDate();
    if (this.selectedScheduleDate < today) return 96;
    if (this.selectedScheduleDate > today) return 0;
    const now = new Date();
    return Math.floor((now.getHours() * 60 + now.getMinutes()) / 15);
  }

  private localDate(): string {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60_000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 10);
  }

  get thisWeekDateRange(): string {
    return this.mondayToSundayRange(0);
  }

  get previousWeekDateRange(): string {
    return this.mondayToSundayRange(1);
  }

  private mondayToSundayRange(weeksAgo: number): string {
    const monday = new Date();
    monday.setHours(0, 0, 0, 0);
    monday.setDate(
      monday.getDate() - ((monday.getDay() + 6) % 7) - weeksAgo * 7,
    );
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const format = (date: Date) =>
      date
        .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
        .toUpperCase();
    return `${format(monday)} - ${format(sunday)}`;
  }
}
